import { useEffect, useMemo, useState } from "react";
import { AlarmClock, CalendarDays, Eye, Search as SearchIcon } from "lucide-react";
import type { TaskWithFlags } from "@desk/contracts";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ProjectGlyph, SpaceGlyph } from "@/components/icons";
import { useHierarchy, useReview, useSearch, useSnoozed, useUpcoming } from "@/data/hooks";
import { useOverlay } from "@/data/outbox";
import { applyPatches } from "@/data/outbox-core";
import { useTaskActions } from "@/data/actions";
import { addDays } from "@/lib/dates";
import { buildUpcomingGroups } from "@/lib/upcoming";
import { setParams, useLocation, href } from "@/router";
import { useConcept } from "@/features/concept-provider";
import { TaskListView } from "@/features/list/TaskListView";
import { CustomSnoozeDialog, SnoozePresetItems } from "@/features/snooze/SnoozeControls";
import { useCurrentDestination } from "./ContainerScreens";
import { ScreenError } from "./collection";
import { ContainerGlyph, SpaceIcon } from "@/components/container-icon";

// ───────────────────────── Upcoming ─────────────────────────
export function UpcomingScreen() {
  const { concept } = useConcept();
  const h = useHierarchy();
  const overlay = useOverlay();
  useCurrentDestination(null);
  const { data, error, refetch } = useUpcoming(addDays(h.today, 1), 90);
  const groups = useMemo(() => {
    if (!data) return [];
    const days = data.days.map((d) => ({ ...d, planned: applyPatches(d.planned, overlay), deadlines: applyPatches(d.deadlines, overlay) }));
    return buildUpcomingGroups(days, h.today);
  }, [data, overlay, h.today]);
  return (
    <>
      <concept.PageHeader title="Upcoming" icon={<CalendarDays />} />
      <ScreenError error={error} onRetry={() => void refetch()} />
      {data && <TaskListView groups={groups} ariaLabel="Upcoming" />}
    </>
  );
}

// ───────────────────────── Review ─────────────────────────
const REASON: Record<string, string> = {
  urgent_undated: "Urgent, no date",
  stale_high: "High, untouched",
  stale_medium: "Medium, untouched",
  stale_low: "Untouched for a while",
};

function ReviewActions({ task }: { task: TaskWithFlags }) {
  const a = useTaskActions();
  const h = useHierarchy();
  const [custom, setCustom] = useState(false);
  return (
    <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
      <Button size="xs" variant="ghost" onClick={() => void a.planAdd([task.id])}>
        Add to Today
      </Button>
      <Button size="xs" variant="ghost" onClick={() => void a.stillRelevant(task)}>
        Still relevant
      </Button>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button size="xs" variant="ghost">
            More
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <SnoozePresetItems onPick={(iso) => void a.snooze(task, iso)} onCustom={() => setCustom(true)} />
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void a.cancel(task)}>Cancel task</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DatePicker value={task.deadline} today={h.today} onChange={(d) => void a.update(task, { deadline: d })} align="end" clearLabel="No deadline">
        <Button size="xs" variant="ghost">
          Set deadline
        </Button>
      </DatePicker>
      <CustomSnoozeDialog open={custom} onOpenChange={setCustom} onConfirm={(iso) => void a.snooze(task, iso)} />
    </div>
  );
}

export function ReviewScreen() {
  const { concept } = useConcept();
  const overlay = useOverlay();
  useCurrentDestination(null);
  const { data, error, refetch } = useReview();
  const reasons = useMemo(() => new Map((data?.items ?? []).map((i) => [i.task.id, i.reason])), [data]);
  const tasks = useMemo(
    () =>
      applyPatches(
        (data?.items ?? []).map((i) => i.task),
        overlay,
      ).filter((t) => t.status !== "done" && t.status !== "cancelled" && !t.flags.isSnoozed),
    [data, overlay],
  );
  return (
    <>
      <concept.PageHeader title="Review" icon={<Eye />} />
      <ScreenError error={error} onRetry={() => void refetch()} />
      {/* Until the first answer, an empty list would wrongly say there's nothing to review. */}
      {data && <TaskListView
        groups={[{ key: "review", label: null, tasks }]}
        ariaLabel="Review"
        location={(t) => REASON[reasons.get(t.id) ?? ""] ?? null}
        trailing={(t) => <ReviewActions task={t} />}
        empty={<concept.EmptyState message="Nothing to review." />}
      />}
    </>
  );
}

// ───────────────────────── Snoozed ─────────────────────────
function SnoozedActions({ task }: { task: TaskWithFlags }) {
  const a = useTaskActions();
  const [custom, setCustom] = useState(false);
  return (
    <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
      <Button size="xs" variant="ghost" onClick={() => void a.unsnooze(task)}>
        Unsnooze
      </Button>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button size="xs" variant="ghost">
            Reschedule
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <SnoozePresetItems onPick={(iso) => void a.snooze(task, iso)} onCustom={() => setCustom(true)} />
        </DropdownMenuContent>
      </DropdownMenu>
      <CustomSnoozeDialog open={custom} onOpenChange={setCustom} onConfirm={(iso) => void a.snooze(task, iso)} />
    </div>
  );
}

export function SnoozedScreen() {
  const { concept } = useConcept();
  const overlay = useOverlay();
  useCurrentDestination(null);
  const { data, error, refetch } = useSnoozed();
  const tasks = useMemo(
    () =>
      applyPatches(data ?? [], overlay)
        .filter((t) => !!t.snoozedUntil && t.snoozedUntil > new Date().toISOString())
        .sort((x, y) => (x.snoozedUntil ?? "").localeCompare(y.snoozedUntil ?? "")),
    [data, overlay],
  );
  return (
    <>
      <concept.PageHeader title="Snoozed" icon={<AlarmClock />} />
      <ScreenError error={error} onRetry={() => void refetch()} />
      {data && <TaskListView
        groups={[{ key: "snoozed", label: null, tasks }]}
        ariaLabel="Snoozed"
        trailing={(t) => <SnoozedActions task={t} />}
        empty={<concept.EmptyState message="Nothing is snoozed." />}
      />}
    </>
  );
}

// ───────────────────────── Search ─────────────────────────
export function stateLabel(t: TaskWithFlags): string | null {
  if (t.deletedAt) return "Deleted";
  if (t.status === "done") return "Done";
  if (t.status === "cancelled") return "Cancelled";
  if (t.flags.isSnoozed) return "Snoozed";
  if (t.flags.inSettledContainer) return "Settled";
  return null;
}

export function SearchScreen() {
  const { concept } = useConcept();
  const h = useHierarchy();
  const loc = useLocation();
  const q = loc.params.get("q") ?? "";
  const [value, setValue] = useState(q);
  // The route owns the query: links, Back, and Forward replace the field's text. Typing only
  // differs from q by surrounding spaces, which must not be stripped mid-word.
  useEffect(() => setValue((v) => (v.trim() === q ? v : q)), [q]);
  const { data, error, isFetching, refetch } = useSearch(q);
  useCurrentDestination(null);
  const tasks = data?.tasks ?? [];
  const hasContainers = !!data && (data.spaces.length > 0 || data.projects.length > 0 || data.areas.length > 0);
  return (
    <>
      <concept.PageHeader title="Search" icon={<SearchIcon />} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setParams({ q: value.trim() || undefined });
        }}
        className="mb-5"
      >
        <input
          aria-label="Search tasks"
          autoFocus
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setParams({ q: e.target.value.trim() || undefined });
          }}
          placeholder="Title, notes, or ID like HUM-12"
          className="h-10 w-full rounded-lg border border-input bg-card px-3 text-[15px] outline-none focus-visible:border-foreground/40"
        />
      </form>
      <ScreenError error={error} onRetry={() => void refetch()} />
      {hasContainers && (
        <div className="mb-5 flex flex-wrap gap-1.5">
          {data.areas.map((a) => (
            <a key={a.id} href={href({ name: "area", id: a.id })} className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border px-2 text-[13px] hover:bg-hover">
              <ContainerGlyph of={a} className="size-4" fallback={<SpaceGlyph name={a.name} className="size-4 text-[9px]" />} />
              {a.name}
            </a>
          ))}
          {data.spaces.map((s) => (
            <a key={s.id} href={href({ name: "space", id: s.id })} className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border px-2 text-[13px] hover:bg-hover">
              <SpaceIcon space={s} className="size-4 text-[9px]" />
              {s.name}
              {(s.archivedAt || s.settledAt) && <span className="text-subtle">{s.archivedAt ? "Archived" : "Settled"}</span>}
            </a>
          ))}
          {data.projects.map((p) => (
            <a key={p.id} href={href({ name: "project", id: p.id })} className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border px-2 text-[13px] hover:bg-hover">
              <ProjectGlyph state={p.state} />
              {p.name}
              {p.archivedAt && <span className="text-subtle">Archived</span>}
            </a>
          ))}
        </div>
      )}
      {q && (data || !error) && (
        <TaskListView
          groups={[{ key: "results", label: null, tasks }]}
          ariaLabel="Search results"
          location={(t) => [stateLabel(t), h.space(t.spaceId)?.name ?? (t.areaId ? h.area(t.areaId)?.name : "Inbox")].filter(Boolean).join(" · ")}
          empty={
            <concept.EmptyState
              message={
                !data && isFetching
                  ? "Searching…"
                  : // Matching areas, spaces, or projects are listed above, so only the tasks came up empty.
                    hasContainers
                    ? "No matching tasks."
                    : `No results for “${q}”.`
              }
            />
          }
        />
      )}
    </>
  );
}

